import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  ReactNode,
} from "react";
import { supabase } from "@/lib/supabase";
import { Tables } from "@/types/database.types";

// Database types we'll use
type Organization = Tables<"organizations">;
type PlanFeatures = Tables<"plan_features">;
type Plan = Tables<"plans">;

// Usage metrics from organization table
type UsageMetrics = Pick<
  Organization,
  | "conversations_this_month"
  | "ai_generations_this_month"
  | "voice_calls_this_month"
  | "voice_minutes_used_this_month"
  | "api_calls_this_month"
  | "active_agents_count"
  | "team_members_count"
  | "storage_usage_mb"
  | "ai_assistant_instances_count"
  | "knowledge_items_count"
  | "files_count"
>;

// Subscription info from organization
type SubscriptionInfo = Pick<
  Organization,
  | "subscription_status"
  | "current_period_end"
  | "stripe_subscription_id"
  | "plan_id"
>;

// Feature types that map to database fields
type FeatureType =
  | "conversations" // -> conversations_this_month vs max_conversations_per_month
  | "ai_generations" // -> ai_generations_this_month vs max_ai_generations_per_month
  | "voice_calls" // -> voice_calls_this_month vs max_voice_calls_per_month
  | "voice_minutes" // -> voice_minutes_used_this_month vs max_voice_minutes_per_month
  | "api_access" // -> api_calls_this_month vs max_api_calls_per_month
  | "active_agents" // -> active_agents_count vs max_active_agents
  | "team_members" // -> team_members_count vs max_team_members
  | "knowledge_items" // -> knowledge_items_count vs max_knowledge_items
  | "storage" // -> storage_usage_mb vs max_storage_mb
  | "ai_assistants" // -> ai_assistant_instances_count vs max_ai_assistant_instances
  | "files" // -> files_count vs max_files
  | "webhook_endpoints" // -> (custom tracking) vs max_webhook_endpoints
  | "custom_branding" // -> can_remove_powered_by (boolean feature)
  | "web_search" // -> can_web_search (boolean feature)
  | "file_collection" // -> can_collect_files (boolean feature)
  | "phone_collection" // -> can_collect_phone (boolean feature)
  | "custom_domain" // -> can_custom_domain (boolean feature)
  | "human_escalation" // -> can_human_escalation (boolean feature)
  | "multi_language" // -> can_multi_language (boolean feature)
  | "team_collaboration" // -> can_team_collaboration (boolean feature)
  | "agents" // -> has_voice_agents (boolean feature) - controls both chat and voice agents
  | "custom_onboarding"; // -> has_custom_onboarding (boolean feature);

// Map feature types to database fields
type UsageMetric = keyof UsageMetrics;
type PlanLimit = keyof PlanFeatures;

interface FeatureAccess {
  allowed: boolean;
  reason?:
    | "unpaid"
    | "limit_reached"
    | "not_in_plan"
    | "free_tier"
    | "past_due";
  message?: string;
  remainingUsage?: number;
  limit?: number;
}

interface FeatureGatingContextValue {
  // Organization data from database
  organization: Organization | null;
  planFeatures: PlanFeatures | null;
  plan: Plan | null;

  // Check if feature is available (plan + payment + limits)
  canUseFeature: (feature: FeatureType) => FeatureAccess;

  // Get remaining usage for a feature
  getRemainingUsage: (metric: UsageMetric) => number;

  // Check if user has pro/paid plan
  hasPaidPlan: () => boolean;

  // Check if specific plan feature is enabled
  isPlanFeatureEnabled: (feature: keyof PlanFeatures) => boolean;

  // Get upgrade message for locked feature
  getUpgradeMessage: (feature: FeatureType) => string;

  // Real-time usage data from organization
  currentUsage: UsageMetrics | null;

  // Plan limits from plan_features table
  planLimits: PlanFeatures | null;

  // Subscription status from organization
  subscriptionStatus: SubscriptionInfo | null;

  // Refresh data from database
  refreshUsageData: () => Promise<void>;

  // Track usage and check limits
  trackUsageAndCheck: (
    feature: FeatureType,
    increment?: number,
  ) => Promise<boolean>;

  // Loading state
  isLoading: boolean;
}

const FeatureGatingContext = createContext<FeatureGatingContextValue | null>(
  null,
);

// Free tier features that don't require payment
const FREE_TIER_FEATURES: FeatureType[] = [
  // These features are available to all users but with limits
];

// Feature mapping to database fields
const FEATURE_MAPPING: Record<
  FeatureType,
  {
    usageField?: keyof UsageMetrics;
    limitField?: keyof PlanFeatures;
    isBooleanFeature?: boolean;
  }
> = {
  conversations: {
    usageField: "conversations_this_month",
    limitField: "max_conversations_per_month",
  },
  ai_generations: {
    usageField: "ai_generations_this_month",
    limitField: "max_ai_generations_per_month",
  },
  voice_calls: {
    usageField: "voice_calls_this_month",
    limitField: "max_voice_calls_per_month",
  },
  voice_minutes: {
    usageField: "voice_minutes_used_this_month",
    limitField: "max_voice_minutes_per_month",
  },
  api_access: {
    usageField: "api_calls_this_month",
    limitField: "max_api_calls_per_month",
  },
  active_agents: {
    usageField: "active_agents_count",
    limitField: "max_active_agents",
  },
  team_members: {
    usageField: "team_members_count",
    limitField: "max_team_members",
  },
  storage: {
    usageField: "storage_usage_mb",
    limitField: "max_storage_mb",
  },
  ai_assistants: {
    usageField: "ai_assistant_instances_count",
    limitField: "max_ai_assistant_instances",
  },
  knowledge_items: {
    usageField: "knowledge_items_count",
    limitField: "max_knowledge_items",
  },
  files: {
    usageField: "files_count",
    limitField: "max_files",
  },
  webhook_endpoints: {
    limitField: "max_webhook_endpoints",
  },
  // Boolean features
  custom_branding: {
    limitField: "can_remove_powered_by",
    isBooleanFeature: true,
  },
  web_search: {
    limitField: "can_web_search",
    isBooleanFeature: true,
  },
  file_collection: {
    limitField: "can_collect_files",
    isBooleanFeature: true,
  },
  phone_collection: {
    limitField: "can_collect_phone",
    isBooleanFeature: true,
  },
  custom_domain: {
    limitField: "can_custom_domain",
    isBooleanFeature: true,
  },
  human_escalation: {
    limitField: "can_human_escalation",
    isBooleanFeature: true,
  },
  multi_language: {
    limitField: "can_multi_language",
    isBooleanFeature: true,
  },
  team_collaboration: {
    limitField: "can_team_collaboration",
    isBooleanFeature: true,
  },
  agents: {
    limitField: "has_agents",
    isBooleanFeature: true,
  },
  custom_onboarding: {
    limitField: "has_custom_onboarding",
    isBooleanFeature: true,
  },
};

// Counter mapping for usage tracking
const COUNTER_MAPPING: Record<FeatureType, string> = {
  conversations: "conversations_this_month",
  ai_generations: "ai_generations_this_month",
  voice_calls: "voice_calls_this_month",
  api_access: "api_calls_this_month",
  active_agents: "active_agents_count",
  team_members: "team_members_count",
  storage: "storage_usage_mb",
  ai_assistants: "ai_assistant_instances_count",
  knowledge_items: "knowledge_items_count",
  files: "files_count",
  voice_minutes: "voice_minutes_used_this_month",
  webhook_endpoints: "", // Not implemented yet
  custom_branding: "",
  web_search: "",
  file_collection: "",
  phone_collection: "",
  custom_domain: "",
  human_escalation: "",
  multi_language: "",
  team_collaboration: "",
  agents: "",
  custom_onboarding: "",
};

interface FeatureGatingProviderProps {
  children: ReactNode;
  organizationId: string;
}

export function FeatureGatingProvider({
  children,
  organizationId,
}: FeatureGatingProviderProps) {
  const [organization, setOrganization] = useState<Organization | null>(null);
  const [planFeatures, setPlanFeatures] = useState<PlanFeatures | null>(null);
  const [plan, setPlan] = useState<Plan | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Helper functions
  const hasPaidPlan = useCallback((): boolean => {
    if (!organization) return false;
    const validStatuses = ["active", "trialing"];
    return validStatuses.includes(organization.subscription_status || "");
  }, [organization]);

  const isPastDue = useCallback((): boolean => {
    return organization?.subscription_status === "past_due";
  }, [organization]);

  const isFreeTierFeature = useCallback((feature: FeatureType): boolean => {
    return FREE_TIER_FEATURES.includes(feature);
  }, []);

  const getRemainingUsage = useCallback(
    (metric: UsageMetric): number => {
      if (!organization || !planFeatures) return 0;

      const currentUsage = organization[metric] || 0;
      const limitFieldMap: Record<UsageMetric, keyof PlanFeatures> = {
        conversations_this_month: "max_conversations_per_month",
        ai_generations_this_month: "max_ai_generations_per_month",
        voice_calls_this_month: "max_voice_calls_per_month",
        voice_minutes_used_this_month: "max_voice_minutes_per_month",
        api_calls_this_month: "max_api_calls_per_month",
        active_agents_count: "max_active_agents",
        team_members_count: "max_team_members",
        storage_usage_mb: "max_storage_mb",
        ai_assistant_instances_count: "max_ai_assistant_instances",
        knowledge_items_count: "max_knowledge_items",
        files_count: "max_files",
      };

      const limitField = limitFieldMap[metric];
      if (!limitField) return 0;

      const limit = planFeatures[limitField] as number;

      // If limit is null, undefined, or 0, treat as unlimited
      if (!limit || limit === 0) {
        return Infinity;
      }

      return Math.max(0, limit - (currentUsage as number));
    },
    [organization, planFeatures],
  );

  const isPlanFeatureEnabled = useCallback(
    (feature: keyof PlanFeatures): boolean => {
      if (!planFeatures) return false;
      return Boolean(planFeatures[feature]);
    },
    [planFeatures],
  );

  const getUpgradeMessage = useCallback((feature: FeatureType): string => {
    const featureNames: Record<FeatureType, string> = {
      conversations: "conversations",
      ai_generations: "AI generations",
      voice_calls: "voice calls",
      voice_minutes: "voice minutes",
      api_access: "API access",
      active_agents: "active agents",
      team_members: "team members",
      knowledge_items: "knowledge items",
      storage: "storage",
      ai_assistants: "AI assistants",
      files: "file uploads",
      webhook_endpoints: "webhook endpoints",
      custom_branding: "custom branding",
      web_search: "web search",
      file_collection: "file collection",
      phone_collection: "phone number collection",
      custom_domain: "custom domain",
      human_escalation: "human escalation",
      multi_language: "multi-language support",
      team_collaboration: "team collaboration",
      agents: "agent functionality",
      custom_onboarding: "custom onboarding",
    };

    const featureName = featureNames[feature] || feature;
    return `Upgrade your plan to unlock ${featureName}`;
  }, []);

  const canUseFeature = useCallback(
    (feature: FeatureType): FeatureAccess => {
      if (!organization || !planFeatures) {
        return { allowed: false, reason: "unpaid", message: "Loading..." };
      }

      const mapping = FEATURE_MAPPING[feature];

      // Check payment status first
      if (isPastDue()) {
        return {
          allowed: false,
          reason: "past_due",
          message:
            "Your subscription is past due. Please update your payment method to continue.",
        };
      }

      if (!hasPaidPlan() && !isFreeTierFeature(feature)) {
        return {
          allowed: false,
          reason: "unpaid",
          message:
            "This feature requires an active subscription. Upgrade to unlock.",
        };
      }

      // Check boolean features (plan-based features)
      if (mapping?.isBooleanFeature && mapping.limitField) {
        const isEnabled =
          planFeatures[mapping.limitField as keyof PlanFeatures];
        if (!isEnabled) {
          return {
            allowed: false,
            reason: "not_in_plan",
            message:
              "This feature is not available in your current plan. Upgrade to access.",
          };
        }
        return { allowed: true };
      }

      // Check usage-based features
      if (mapping?.usageField && mapping?.limitField) {
        const currentUsage = organization[mapping.usageField] || 0;
        const limit = planFeatures[mapping.limitField] as number;

        // If limit is null, undefined, or 0, treat as unlimited
        if (!limit || limit === 0) {
          return {
            allowed: true,
            remainingUsage: Infinity,
            limit: null,
          };
        }

        if (currentUsage >= limit) {
          return {
            allowed: false,
            reason: "limit_reached",
            message: `You've reached your monthly limit of ${limit} ${feature.replace("_", " ")}`,
            remainingUsage: 0,
            limit,
          };
        }

        return {
          allowed: true,
          remainingUsage: limit - currentUsage,
          limit,
        };
      }

      return { allowed: true };
    },
    [organization, planFeatures, hasPaidPlan, isPastDue, isFreeTierFeature],
  );

  // Refresh data from database
  const refreshUsageData = useCallback(async () => {
    if (!organizationId) return;

    try {
      setIsLoading(true);

      // Fetch fresh organization data
      const { data: orgData, error: orgError } = await supabase
        .from("organizations")
        .select("*")
        .eq("id", organizationId)
        .single();

      if (orgError) {
        console.error("Error fetching organization:", orgError);
        return;
      }

      setOrganization(orgData);

      // Fetch plan and plan features if plan_id exists
      if (orgData?.plan_id) {
        const [planResult, planFeaturesResult] = await Promise.all([
          supabase.from("plans").select("*").eq("id", orgData.plan_id).single(),
          supabase
            .from("plan_features")
            .select("*")
            .eq("plan_id", orgData.plan_id)
            .single(),
        ]);

        if (!planResult.error) setPlan(planResult.data);
        if (!planFeaturesResult.error) setPlanFeatures(planFeaturesResult.data);
      }
    } catch (error) {
      console.error("Error refreshing usage data:", error);
    } finally {
      setIsLoading(false);
    }
  }, [organizationId]);

  // Track usage and check limits
  const trackUsageAndCheck = useCallback(
    async (feature: FeatureType, increment: number = 1): Promise<boolean> => {
      if (!organizationId) return false;

      const counterName = COUNTER_MAPPING[feature];
      if (!counterName) return false;

      try {
        // This will automatically update the organization row
        await supabase.rpc("increment_organization_counter", {
          org_id: organizationId,
          counter_name: counterName,
          increment_by: increment,
        });

        return true;
      } catch (error) {
        console.error("Failed to track usage:", error);
        return false;
      }
    },
    [organizationId],
  );

  // Initial data load
  useEffect(() => {
    if (organizationId) {
      refreshUsageData();
    }
  }, [organizationId, refreshUsageData]);

  // Real-time subscriptions
  useEffect(() => {
    if (!organizationId) return;

    // Subscribe to organization changes (usage counters)
    const orgSubscription = supabase
      .channel(`organization-usage-${organizationId}`)
      .on(
        "postgres_changes",
        {
          event: "UPDATE",
          schema: "public",
          table: "organizations",
          filter: `id=eq.${organizationId}`,
        },
        (payload) => {
          setOrganization(payload.new as Organization);
        },
      )
      .subscribe();

    // Subscribe to plan_features changes (if plan changes)
    const planSubscription = supabase
      .channel(`plan-features-${organizationId}`)
      .on(
        "postgres_changes",
        {
          event: "UPDATE",
          schema: "public",
          table: "plan_features",
        },
        (payload) => {
          if (organization?.plan_id === payload.new.plan_id) {
            setPlanFeatures(payload.new as PlanFeatures);
          }
        },
      )
      .subscribe();

    return () => {
      orgSubscription.unsubscribe();
      planSubscription.unsubscribe();
    };
  }, [organizationId, organization?.plan_id]);

  const currentUsage: UsageMetrics | null = organization
    ? {
        conversations_this_month: organization.conversations_this_month,
        ai_generations_this_month: organization.ai_generations_this_month,
        voice_calls_this_month: organization.voice_calls_this_month,
        voice_minutes_used_this_month:
          organization.voice_minutes_used_this_month,
        api_calls_this_month: organization.api_calls_this_month,
        active_agents_count: organization.active_agents_count,
        team_members_count: organization.team_members_count,
        storage_usage_mb: organization.storage_usage_mb,
        ai_assistant_instances_count: organization.ai_assistant_instances_count,
        knowledge_items_count: organization.knowledge_items_count,
        files_count: organization.files_count,
      }
    : null;

  const subscriptionStatus: SubscriptionInfo | null = organization
    ? {
        subscription_status: organization.subscription_status,
        current_period_end: organization.current_period_end,
        stripe_subscription_id: organization.stripe_subscription_id,
        plan_id: organization.plan_id,
      }
    : null;

  const value: FeatureGatingContextValue = {
    organization,
    planFeatures,
    plan,
    canUseFeature,
    getRemainingUsage,
    hasPaidPlan,
    isPlanFeatureEnabled,
    getUpgradeMessage,
    currentUsage,
    planLimits: planFeatures,
    subscriptionStatus,
    refreshUsageData,
    trackUsageAndCheck,
    isLoading,
  };

  return (
    <FeatureGatingContext.Provider value={value}>
      {children}
    </FeatureGatingContext.Provider>
  );
}

export function useFeatureGating(): FeatureGatingContextValue {
  const context = useContext(FeatureGatingContext);
  if (!context) {
    throw new Error(
      "useFeatureGating must be used within a FeatureGatingProvider",
    );
  }
  return context;
}

export type { FeatureType, FeatureAccess, UsageMetric };
